import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  totpToken?: string;
}

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { login } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setError(null);

    try {
      await login(data.email, data.password, data.totpToken);
      toast.success('Login successful!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Login failed';

      if (errorMessage === '2FA token required') {
        setRequire2FA(true);
        setError('Please enter your 2FA token');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequire2FA(false);
    setError(null);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="grey.100"
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Clinical Therapy App
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" gutterBottom>
          {require2FA ? 'Two-Factor Authentication' : 'Login'}
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
          {!require2FA ? (
            <>
              <TextField
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                fullWidth
                label="Email"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                margin="normal"
                autoComplete="email"
                autoFocus
              />

              <TextField
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password?.message}
                margin="normal"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <TextField
              {...register('totpToken', {
                required: '2FA token is required',
                pattern: {
                  value: /^\d{6}$/,
                  message: '2FA token must be 6 digits',
                },
              })}
              fullWidth
              label="2FA Token"
              type="text"
              inputProps={{ maxLength: 6 }}
              error={!!errors.totpToken}
              helperText={errors.totpToken?.message || 'Enter the 6-digit code from your authenticator app'}
              margin="normal"
              autoFocus
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            {require2FA && (
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Back
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth={!require2FA}
              disabled={loading}
              sx={require2FA ? { flex: 1 } : {}}
            >
              {loading ? 'Signing in...' : require2FA ? 'Verify' : 'Sign In'}
            </Button>
          </Box>

          {!require2FA && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="text"
                size="small"
                onClick={() => window.location.href = '/forgot-password'}
                disabled={loading}
              >
                Forgot Password?
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;